<template>
  <div>
    <v-row>
      <v-col cols="6">
        <h3 class="ml-2 mb-2">基本情報</h3>
        <v-form ref="widgetForm">
          <v-text-field
            v-model="widget.name"
            label="ウィジェット名"
            class="mb-3"
            required
            :rules="[presenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="ウィジェット名" :required="true" />
            </template>
          </v-text-field>
          <v-select
            v-model="widget.device"
            :items="devices"
            class="body-2 mb-3"
            style="width: 300px"
            label="デバイス"
            dense
            :rules="[presenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="デバイス" :required="true" />
            </template>
          </v-select>
          <v-select
            v-model="widget.morality"
            :items="moralities"
            class="body-2 mb-3"
            style="width: 300px"
            label="道徳性"
            dense
            :rules="[presenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="道徳性" :required="true" />
            </template>
          </v-select>
          <v-select
            v-model="widget.page"
            :items="pages"
            class="body-2 mb-3"
            style="width: 300px"
            label="表示ページ"
            dense
            :rules="[presenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="表示ページ" :required="true" />
            </template>
          </v-select>
          <v-card outlined>
            <v-card-title style='font-size: 16px;'>このウィジェットを特定の記事のみに表示する</v-card-title>
            <v-card-text style='font-size: 12px;'>ウィジェットを特定の記事だけに表示したい場合は、こちらから記事を選択してください。</v-card-text>
            <template v-for="mapWidgetPost in widget.mapWidgetPosts">
              <v-card
                :key="mapWidgetPost.post.id"
                class="mb-2 mx-3"
                outlined
                tile
                v-if="!mapWidgetPost.destroy"
                style='display: flex;'
              >
                <v-img
                  :src='eyecatchableSrc(mapWidgetPost.post)'
                  max-height="60"
                  max-width="60"
                  class='mt-1 ml-2'
                ></v-img>
                <v-card-text class="d-flex justify-space-between">
                  <p class="caption">
                    {{ mapWidgetPost.post.title }}
                  </p>
                  <v-btn icon @click="deleteMapWidgetPost(mapWidgetPost)">
                    <v-icon> mdi-delete </v-icon>
                  </v-btn>
                </v-card-text>
              </v-card>
            </template>
            <div class="text-center">
              <v-btn
                tile
                color="warning"
                dark
                depressed
                class='mb-5'
                @click="postGallary = true"
              >
                <v-icon left small> mdi-plus </v-icon>
                <span class="font-weight-bold">追加</span>
              </v-btn>
            </div>
            <post-gallary
              :post-gallary="postGallary"
              :selectdPostIds='notDestroyPostIds'
              @close-post-gallary="postGallary = false"
              @select-post="selectPost"
            />
          </v-card>
          <v-card class='mt-3' outlined>
            <v-card-title style='font-size: 16px;'>このウィジェットを特定のタグの記事のみに表示する</v-card-title>
            <v-card-text style='font-size: 12px;'>
              ウィジェットを特定のタグに紐づく記事だけに表示したい場合は、こちらからタグを選択してください。
            </v-card-text>
            <v-card outlined class="mb-2 mx-3 px-3">
              <SelectableTags v-model="selectedTags" label="タグ"></SelectableTags>
            </v-card>
          </v-card>
          <v-select
            v-model="widget.position"
            :items="positions"
            class="body-2 mb-3 mt-5"
            style="width: 300px"
            label="ポジション"
            dense
            :rules="[presenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="ポジション" :required="true" />
            </template>
          </v-select>
          <v-checkbox v-model="enabled" label="固定の幅と高さを設定する" />
          <div class="d-flex">
            <v-text-field
              v-model="widget.width"
              :disabled="!enabled"
              label="ウィジェット幅"
              class="mt-0 mb-3 pt-0"
              type="number"
              min="0"
              suffix="px"
              style="max-width: 170px"
            >
            </v-text-field>
            <v-text-field
              v-model="widget.height"
              :disabled="!enabled"
              label="ウィジェット高さ"
              class="mt-0 mb-3 ml-8 pt-0"
              type="number"
              min="0"
              suffix="px"
              style="max-width: 170px"
            >
            </v-text-field>
          </div>
          <v-text-field
            v-model="widget.pageNumber"
            label="ページ番号"
            class="mb-3"
            required
            type="number"
            :rules="[positiveNumberPresenceValidation]"
          >
            <template v-slot:label>
              <InputLabel labelName="ページ番号" :required="true" />
            </template>
          </v-text-field>
          <v-alert
            dense
            outlined
            class="mt-5"
            type="error"
            style="width: 400px"
          >
            <p class="caption">
              記事ページにて、特定のページのみにウィジェットを表示させる場合は任意の数字を入力して下さい。
            </p>
            <p class="caption">それ以外の場合は 0 を入力してください。</p>
          </v-alert>
        </v-form>
      </v-col>
      <v-col cols="6">
        <v-row cols="13">
          <v-btn
            class="text-body-2 widget-add-image-button"
            color="primary darken-1"
            @click="insertImage"
          >
            画像挿入
          </v-btn>
        </v-row>
        <widget-setting-image
          :add-image-dialog="addImageDialog"
          @close-add-image-dialog="addImageDialog = false"
          @insert-image-and-link="insertImageAndLink"
        />
        <h3 class="ml-2 mb-2">
          ウィジェット内容<v-chip
            x-small
            label
            outlined
            class="ml-2"
            color="error"
          >
            必須
          </v-chip>
        </h3>
        <template v-slot:label>
          <InputLabel labelName="ウィジェット内容" :required="true" />
        </template>
        <codemirror v-model="widget.content" :options="cmOptions" ref="basic" />
        <v-btn
          class="px-10 ml-auto text-body-2 float-right"
          color="primary darken-1"
          min-height="50px"
          max-width="100px"
          style="margin-top: 120px"
          tile
          dark
          depressed
          @click="saveWidget"
        >
          保存
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import formValidationUtils from '@/mixins/form_validation_utils'
import InputLabel from '@/components/commons/formParts/inputLabel'
import WidgetSettingImage from '@/components/commons/WidgetSettingImage'
import { codemirror } from 'vue-codemirror'
// language
import 'codemirror/mode/htmlmixed/htmlmixed.js'

// theme css
import 'codemirror/theme/darcula.css'

// require active-line.js
import 'codemirror/addon/selection/active-line.js'

// styleSelectedText
import 'codemirror/addon/selection/mark-selection.js'
import 'codemirror/addon/search/searchcursor.js'

// search
import 'codemirror/addon/dialog/dialog.css'
import 'codemirror/addon/dialog/dialog.js'
import 'codemirror/addon/search/search.js'
import 'codemirror/addon/search/jump-to-line.js'

// lint
import 'codemirror/addon/lint/lint.js'
import 'codemirror/addon/lint/html-lint.js'
import 'codemirror/addon/lint/css-lint.js'
import 'codemirror/addon/lint/javascript-lint.js'
import 'codemirror/addon/lint/lint.css'

// hint
import 'codemirror/addon/hint/show-hint.js'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/html-hint.js'
import 'codemirror/addon/hint/css-hint.js'
import 'codemirror/addon/hint/javascript-hint.js'

// complete
import 'codemirror/addon/edit/closetag.js'
import 'codemirror/addon/edit/closebrackets.js'

// keyMap
import 'codemirror/mode/clike/clike.js'
import 'codemirror/addon/edit/matchbrackets.js'
import PostGallary from '@/components/commons/PostGallary'
import SelectableTags from '../posts/SelectableTags'

export default {
  name: 'WidgetForm',
  components: {
    InputLabel,
    WidgetSettingImage,
    codemirror,
    PostGallary,
    SelectableTags
  },
  mixins: [formValidationUtils],
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  data: () => {
    return {
      addImageDialog: false,
      targetCursor: '',
      target: '_blank',
      devices: [
        { text: '共通', value: 'all_device', intValue: 0 },
        { text: 'SP（モバイル）', value: 'mobile', intValue: 2 },
        { text: 'PC・その他', value: 'other_device', intValue: 1 }
      ],
      pages: [
        { text: '全てのページ', value: 'all_page', intValue: 0 },
        { text: 'トップページ', value: 'top_page', intValue: 1 },
        { text: '記事ページ', value: 'posts_show', intValue: 3 }
      ],
      moralities: [
        { text: '共通表示', value: 'either_morality', intValue: 0 },
        { text: '道徳的なページのみ表示', value: 'moral', intValue: 1 },
        { text: '非道徳的なページのみ表示', value: 'immoral', intValue: 2 }
      ],
      positions: [
        { text: 'headタグ内', value: 'in_head', intValue: 1 },
        { text: 'bodyタグ内', value: 'in_body', intValue: 2 },
        { text: '目次上', value: 'under_content_header', intValue: 3 },
        { text: '3番目h2前', value: 'before_third_h2_content', intValue: 16 },
        { text: '5番目h2前', value: 'before_fifth_h2_content', intValue: 17 },
        { text: 'コンテンツ上', value: 'on_content', intValue: 4 },
        { text: 'コンテンツ内1', value: 'in_content_1', intValue: 5 },
        { text: 'コンテンツ内2', value: 'in_content_2', intValue: 6 },
        { text: 'コンテンツ内3', value: 'in_content_3', intValue: 7 },
        { text: 'コンテンツ下', value: 'under_content', intValue: 8 },
        {
          text: 'コンテンツフッター下',
          value: 'under_content_footer',
          intValue: 9
        },
        { text: 'サイドバー上', value: 'on_sidebar', intValue: 10 },
        { text: 'サイドバー中', value: 'in_sidebar', intValue: 11 },
        { text: 'サイドバー下', value: 'under_sidebar', intValue: 12 },
        { text: 'bodyタグ内最上部', value: 'top_body', intValue: 13 },
        { text: 'コンテンツ最後部', value: 'content_bottom', intValue: 14 },
        { text: 'フッター上', value: 'footer_top', intValue: 15 }
      ],
      cmOptions: {
        mode: 'htmlmixed',
        line: true,
        lineNumbers: true,
        collapseIdentical: false,
        highlightDifferences: true,
        matchBrackets: true,
        showCursorWhenSelecting: true,
        theme: 'darcula',
        lint: true,
        lintOnChange: true,
        selfContain: true,
        highlightLines: true,
        autoCloseTags: true,
        autoCloseBrackets: true,
        completeOnSingleClick: true,
        viewportMargin: 20,
        hintOptions: {
          completeSingle: false
        }
      },
      enabled: false,
      postGallary: false
    }
  },
  mounted () {
    if (this.widget.width || this.widget.height) {
      this.enabled = true
    }
  },
  computed: {
    notDestroyPostIds: function () {
      let ids = []
      if (this.widget.mapWidgetPosts) {
        this.widget.mapWidgetPosts.forEach((item) => {
          if (!item.destroy) {
            ids.push(item.post.id)
          }
        })
      }
      return ids
    },
    eyecatchableSrc () {
      return function (post) {
        if (post.eyecatchable) {
          return post.eyecatchable.file.middle.url
        } else {
          return 'http://placehold.jp/eeeeee/cccccc/200x200.png?text=No%20Image'
        }
      }
    },
    selectedTags: {
      get () {
        if (this.widget.tags) {
          return this.widget.tags
        } else {
          return []
        }
      },
      set (localTags) {
        if (!this.widget.mapWidgetTags) {
          this.widget.mapWidgetTags = []
        }
        let remoteTagIds = []
        this.widget.mapWidgetTags.map((item) => {
          remoteTagIds.push(item.tag.id)
        })
        let localTagIds = []
        localTags.map((tag) => {
          localTagIds.push(tag.id)
        })
        this.widget.mapWidgetTags.map((item) => {
          if (item.id && !localTagIds.includes(item.tag.id)) {
            item.destroy = true
          } else {
            item.destroy = false
          }
        })
        const newTags = localTagIds.filter(item => !remoteTagIds.includes(item))
        newTags.forEach((item) => {
          this.widget.mapWidgetTags.push({
            destroy: false,
            tag: {
              id: item
            }
          })
        })
        this.$emit('update-widget', this.widget)
      }
    }
  },
  methods: {
    saveWidget () {
      if (!this.$refs.widgetForm.validate()) {
        return false
      }
      if (!this.enabled) {
        this.widget.width = null; this.widget.height = null
      }

      this.$emit('save-widget')
    },
    setTargetCursor () {
      this.targetCursor = this.$refs.basic.codemirror.getDoc()
    },
    insertImageAndLink (image, link) {
      var htmlCode = ''
      if (link) {
        htmlCode = `<a href="${link}" target="${this.target}">\n  <img src= "${image}">\n</a>`
      } else if (image) {
        htmlCode += `<img src= "${image}">`
      }

      const cur = this.targetCursor.getCursor()
      if (cur.line === 0 && cur.ch === 0) {
        this.widget.content = this.widget.content + '\n' + htmlCode
      } else {
        this.targetCursor.replaceRange(htmlCode, { line: cur.line, ch: cur.ch })
      }
    },
    selectPost (post) {
      let mapWidgetPostIds = []
      let mapWidgetPost
      if (this.widget.mapWidgetPosts) {
        mapWidgetPostIds = this.widget.mapWidgetPosts.map((item) => item.post.id)
        mapWidgetPost = this.widget.mapWidgetPosts.find((item) => {
          return item.post.id == post.id
        })
      }
      if (mapWidgetPostIds.includes(post.id) && mapWidgetPost.destroy == false) {
        const payload = {
          status: 500,
          message: 'この記事はすでに選択されています。'
        }
        this.$store.dispatch('SystemMessageStore/createMessage', payload)
      } else if (mapWidgetPostIds.includes(post.id) && mapWidgetPost.destroy == true) {
        mapWidgetPost.destroy = false
      } else {
        if (this.widget.mapWidgetPosts) {
          this.widget.mapWidgetPosts.push({
            post: post,
            destroy: false
          })
        } else {
          this.widget.mapWidgetPosts = [{post: post, destroy: false}]
        }
      }
    },
    deleteMapWidgetPost (mapWidgetPost) {
      this.widget.mapWidgetPosts.find(item => item.post.id === mapWidgetPost.post.id).destroy = true
      this.$emit('update-widget', this.widget)
    },
    insertImage () {
      this.setTargetCursor()
      this.addImageDialog = true
    }
  }
}
</script>

<style scoped>
.widget-add-image-button {
  margin-left: 80%;
}
</style>
